/**
 * The download page's "Deliver it yourself" panel, sheet 30 callouts 8 and 10
 * (mission 7 WP11 T059; FR-016; TX.1, TX.2, TX.9, X.1, UC-01;
 * contracts/ui-api.md "The touched mission 6 screens").
 *
 * It reads `bureausQuery()` and lists every bureau whose effective status is
 * not `verified`, each with its status badge and the reason: the kill-switch
 * sentence for a bureau Trellis has disabled (TX.9), else the transmit table's
 * `rowReason.<status>`. Then one link to the credentials page (sheet 41).
 *
 * - Nothing renders when all four bureaus are verified, while the read is in
 *   flight, or when it fails: the panel is an enrichment of the download page,
 *   which must not gain an error band for it.
 * - No portal links: 30/9 waits for Legal (index open question 55), and no
 *   copy here implies Bloom acts for the reporter with a bureau (X.1).
 * - Download.tsx renders it for a ready Path A run only (U22).
 */
import { useId } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { BureauStatusBadge } from '@/components/trellis/BureauStatusBadge'
import { bureauName, type BureauEntry, type CredentialStatus } from '@/lib/api/bureaus'
import { copyText, type TextKey } from '@/lib/provisional-copy'
import { bureausQuery } from '@/lib/queries'

type Unverified = Exclude<CredentialStatus, 'verified'>

const REASON: Record<Unverified, TextKey> = {
  not_entered: 'rowReason.notEntered',
  entered_untested: 'rowReason.enteredUntested',
  auth_failed: 'rowReason.authFailed',
  disabled_by_trellis: 'killSwitchExplanation',
}

/** The kill switch wins over the stored status (TX.9). */
function reason(entry: BureauEntry & { status: Unverified }): string {
  return copyText(entry.disabled_by_trellis ? 'killSwitchExplanation' : REASON[entry.status])
}

function isUnverified(entry: BureauEntry): entry is BureauEntry & { status: Unverified } {
  return entry.status !== 'verified'
}

export function DeliverYourself() {
  const headingId = useId()
  const bureaus = useQuery(bureausQuery())
  const missing = (bureaus.data?.bureaus ?? []).filter(isUnverified)
  if (missing.length === 0) return null

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-4 rounded-lg border border-navy-200 bg-white p-6">
      <h2 id={headingId} className="text-h3 text-navy-950">
        {copyText('deliverYourselfHeading')}
      </h2>
      <ul className="flex flex-col gap-3">
        {missing.map((entry) => (
          <li key={entry.bureau} data-deliver-bureau={entry.bureau} className="flex flex-col gap-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-body font-medium text-navy-950">{bureauName(entry.bureau)}</span>
              <BureauStatusBadge status={entry.status} />
            </span>
            <p className="text-body-sm text-navy-600">{reason(entry)}</p>
          </li>
        ))}
      </ul>
      <div>
        <Link
          to="/app/settings/bureaus"
          className="text-body text-[var(--color-link)] underline-offset-4 hover:text-[var(--color-link-hover)] hover:underline"
        >
          {copyText('manageBureauCredentials')}
        </Link>
      </div>
    </section>
  )
}
